// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Atomi di scrittura delle guide del Centro assistenza
//
// Tutto quello che serve per scrivere una guida senza programmare. Chi aggiunge
// una guida nuova importa da qui e scrive solo contenuto.
//
//   import { GuidaLayout, H, P, Strong, Code, Callout, Passo, MockFrame,
//            Schermo, FAQ } from './atomi';
//
// NOTA STORICA: questi atomi nascono dentro guide/PrimiPassi.js in copia locale.
// La migrazione e' stata fatta il 17/08/2026: ora tutte le guide, PrimiPassi
// compresa, importano da qui. Non reintrodurre copie locali — due MockFrame
// diversi finirebbero per divergere senza che nessuno capisca perche'.
//
// ─── LE DUE FORME DI UNA SCHERMATA ──────────────────────────────────────────
// MockFrame accetta due modi di mostrare una schermata:
//
//   1. SCREENSHOT REALE — <MockFrame imageSrc="/screenshots/x.png" markers=... />
//      Vero per definizione nel momento in cui e' stato scattato, ma invecchia
//      in silenzio e pesa. Si usa solo per le schermate troppo dense da
//      ridisegnare (dettaglio lavorazione, bilancio di massa).
//
//   2. RICOSTRUZIONE — <MockFrame url="..."><Schermo>...</Schermo></MockFrame>
//      Disegnata con i token di design veri dell'app, quindi colori e spaziature
//      sono quelli. Mostra solo cio' che interessa al passo, resta testo (si
//      cerca, si legge sul telefono, entra nel PDF), non pesa e non puo'
//      contenere per sbaglio i dati di un cliente vero.
//
//      PERICOLO: una ricostruzione e' vera solo quanto chi l'ha scritta, e
//      quando e' falsa non si vede. La vecchia help/screens/MockScreens.js
//      mostrava un pulsante "Esegui" che nell'app non esiste. Regola: le
//      etichette si copiano dal codice dell'app, mai a memoria, e ogni guida
//      va confrontata una volta con la schermata vera prima di pubblicarla.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import { CatTile } from '../../ui/components';

// ─────── Testo ───────────────────────────────────────────────────────────────

export const Strong = ({ children }) => (
  <strong style={{ color: LT.n900, fontWeight: 600 }}>{children}</strong>
);

export const Code = ({ children }) => (
  <code style={{
    fontFamily: LT.mono, fontSize: 13, padding: '2px 6px',
    background: LT.n100, borderRadius: 4, color: LT.n800,
  }}>{children}</code>
);

export const P = ({ children, style }) => (
  <p style={{ fontSize: 15.5, color: LT.n700, lineHeight: 1.7, margin: '12px 0', ...style }}>
    {children}
  </p>
);

export const H = ({ id, cat, children }) => (
  <h2 id={id} style={{
    fontSize: 26, fontWeight: 700, color: LT.n900, letterSpacing: '-0.02em',
    margin: '48px 0 12px', display: 'flex', alignItems: 'center', gap: 14,
    scrollMarginTop: 80,
  }}>
    {cat && <CatTile cat={cat} size={36} />}
    <span>{children}</span>
  </h2>
);

// Il nome letterale di un comando a schermo. Si usa ogni volta che si dice
// all'utente cosa premere: mai descrivere un pulsante a parole, si cita.
export const Tasto = ({ children }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    fontFamily: LT.sans, fontSize: 13.5, fontWeight: 600,
    padding: '2px 8px', borderRadius: 6,
    background: LT.n0, color: LT.n800,
    border: `1px solid ${LT.n300}`, boxShadow: '0 1px 0 rgba(15,23,42,.06)',
    whiteSpace: 'nowrap',
  }}>{children}</span>
);

export const Callout = ({ kind = 'info', titolo, children }) => {
  const M = {
    info:    { c: LT.blu,     bg: LT.bluSoft,    iconD: I.info  },
    success: { c: LT.verde,   bg: LT.verdeSoft,  iconD: I.check },
    warn:    { c: LT.arancio, bg: LT.arancioSoft, iconD: I.alert },
    tip:     { c: LT.viola,   bg: LT.violaSoft,  iconD: I.sparkle },
  }[kind] || {};
  return (
    <div style={{
      padding: '14px 16px', background: M.bg, borderRadius: LT.r.md,
      borderLeft: `3px solid ${M.c}`, margin: '18px 0',
      display: 'flex', gap: 12, alignItems: 'flex-start',
    }}>
      <Ico d={M.iconD} size={18} stroke={M.c} style={{ marginTop: 2 }} />
      <div>
        {titolo && (
          <div style={{ fontWeight: 700, color: M.c, fontSize: 13, marginBottom: 4 }}>
            {titolo}
          </div>
        )}
        <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6 }}>{children}</div>
      </div>
    </div>
  );
};

// ─────── Passo numerato ──────────────────────────────────────────────────────

export const Passo = ({ n, titolo, children }) => (
  <div style={{ display: 'flex', gap: 14, margin: '22px 0' }}>
    <div style={{
      width: 28, height: 28, borderRadius: 999, flexShrink: 0,
      background: LT.blu, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, fontWeight: 700, marginTop: 2,
    }}>{n}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      {titolo && (
        <div style={{ fontSize: 16.5, fontWeight: 700, color: LT.n900, marginBottom: 2 }}>
          {titolo}
        </div>
      )}
      <div>{children}</div>
    </div>
  </div>
);

// ─────── Cornice di una schermata ────────────────────────────────────────────

export function MockFrame({ url, imageSrc, imageAlt = '', markers = [], didascalia, children }) {
  return (
    <div style={{
      borderRadius: LT.r.lg, overflow: 'hidden', boxShadow: LT.sh3,
      border: `1px solid ${LT.n200}`, background: '#fff',
      margin: '20px 0', position: 'relative',
    }}>
      {/* Cornice browser */}
      <div style={{
        height: 34, background: LT.n50, borderBottom: `1px solid ${LT.n150}`,
        display: 'flex', alignItems: 'center', padding: '0 12px', gap: 6,
      }}>
        <div style={{ width: 9, height: 9, borderRadius: 999, background: '#FF5F57' }} />
        <div style={{ width: 9, height: 9, borderRadius: 999, background: '#FEBC2E' }} />
        <div style={{ width: 9, height: 9, borderRadius: 999, background: '#28C840' }} />
        {url && (
          <div style={{
            marginLeft: 12, padding: '3px 10px', borderRadius: 999, background: '#fff',
            fontSize: 11, color: LT.n500, fontFamily: LT.mono,
            border: `1px solid ${LT.n200}`,
          }}>{url}</div>
        )}
      </div>

      {/* Corpo: screenshot oppure ricostruzione. I marker si posizionano in
          percentuale, cosi' restano al loro posto a qualunque larghezza. */}
      <div style={{ position: 'relative', background: LT.nBg }}>
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={imageAlt || url}
            style={{ display: 'block', width: '100%', height: 'auto' }}
            loading="lazy"
          />
        ) : children}
        {markers.map((m, i) => (
          <div key={i} style={{
            position: 'absolute', top: m.top, left: m.left,
            width: 30, height: 30, borderRadius: 999,
            background: LT.blu, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700,
            transform: 'translate(-50%, -50%)',
            boxShadow: '0 0 0 4px rgba(37,99,235,.22), 0 4px 12px rgba(15,23,42,.25)',
            fontFamily: LT.sans, zIndex: 2, pointerEvents: 'none',
          }}>{m.n}</div>
        ))}
      </div>

      {/* Spiegazione dei marker */}
      {markers.length > 0 && (
        <div style={{ padding: '14px 18px', background: LT.n50, borderTop: `1px solid ${LT.n150}` }}>
          {markers.map((m, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              fontSize: 13.5, color: LT.n700, lineHeight: 1.55,
              marginBottom: i < markers.length - 1 ? 8 : 0,
            }}>
              <span style={{
                width: 22, height: 22, borderRadius: 999, background: LT.blu, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1,
              }}>{m.n}</span>
              <span><Strong>{m.titolo}.</Strong> {m.testo}</span>
            </div>
          ))}
        </div>
      )}

      {didascalia && !markers.length && (
        <div style={{
          padding: '10px 18px', background: LT.n50, borderTop: `1px solid ${LT.n150}`,
          fontSize: 12.5, color: LT.n500,
        }}>{didascalia}</div>
      )}
    </div>
  );
}

// Contenitore di una schermata ricostruita: riproduce sfondo e respiro dell'app.
export const Schermo = ({ children, style }) => (
  <div style={{
    padding: 18, fontFamily: LT.sans, color: LT.n800, background: LT.nBg,
    ...style,
  }}>{children}</div>
);

// Barra del titolo di una schermata dell'app.
export const SchermoTitolo = ({ titolo, sottotitolo, destra }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', gap: 12,
    marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${LT.n150}`,
  }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 19, fontWeight: 800, color: LT.navy, letterSpacing: -0.2 }}>
        {titolo}
      </div>
      {sottotitolo && (
        <div style={{ fontSize: 12.5, color: LT.n500, marginTop: 3 }}>{sottotitolo}</div>
      )}
    </div>
    {destra}
  </div>
);

// ─────── FAQ ─────────────────────────────────────────────────────────────────

export function FAQ({ q, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${LT.n150}`, padding: '14px 0' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 600, color: LT.n900 }}>{q}</div>
        <Ico
          d={I.chevronD} size={18} stroke={LT.n400}
          style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </div>
      {open && (
        <div style={{ fontSize: 14, color: LT.n600, lineHeight: 1.7, marginTop: 10 }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─────── Impaginazione della guida ───────────────────────────────────────────
// Header + indice laterale con scroll-spy + corpo. Una guida nuova passa
// titolo, sottotitolo, durata, destinatario e l'elenco delle sezioni, e poi
// scrive il contenuto dentro. Gli id delle sezioni devono coincidere con
// quelli degli <H id="...">.

export function GuidaLayout({ titolo, sottotitolo, durata, destinatario, sezioni = [], children }) {
  const [active, setActive] = useState(sezioni[0]?.id);

  useEffect(() => {
    const nodi = sezioni.map(s => document.getElementById(s.id)).filter(Boolean);
    if (!nodi.length) return;
    const obs = new IntersectionObserver(
      entries => {
        const visibili = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visibili[0]) setActive(visibili[0].target.id);
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0.01 }
    );
    nodi.forEach(n => obs.observe(n));
    return () => obs.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const vaiA = (id) => {
    setActive(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={{
      maxWidth: 1180, margin: '0 auto', padding: '0 24px 64px',
      fontFamily: LT.sans, color: LT.n800,
    }}>
      <div style={{ padding: '32px 0 28px' }}>
        <h1 style={{
          fontSize: 38, fontWeight: 700, color: LT.n900,
          letterSpacing: '-0.02em', margin: '0 0 12px', lineHeight: 1.15,
        }}>{titolo}</h1>
        {sottotitolo && (
          <p style={{ fontSize: 16, color: LT.n500, lineHeight: 1.6, maxWidth: 760, margin: 0 }}>
            {sottotitolo}
          </p>
        )}
        <div style={{
          display: 'flex', gap: 14, marginTop: 20, alignItems: 'center',
          fontSize: 13, color: LT.n500, flexWrap: 'wrap',
        }}>
          {durata && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Ico d={I.clock} size={14} />{durata}
            </span>
          )}
          {durata && destinatario && <span style={{ color: LT.n300 }}>·</span>}
          {destinatario && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <Ico d={I.user} size={14} />{destinatario}
            </span>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24 }}>
        <aside
          className="primi-passi-toc"
          style={{
            width: 240, flexShrink: 0,
            position: 'sticky', top: 70, alignSelf: 'flex-start', padding: '8px 0',
          }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: LT.n500,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            marginBottom: 12, paddingLeft: 12,
          }}>
            In questa pagina
          </div>
          {sezioni.map(s => (
            <a
              key={s.id} href={`#${s.id}`}
              onClick={e => { e.preventDefault(); vaiA(s.id); }}
              style={{
                display: 'block', padding: '7px 12px', borderRadius: LT.r.sm,
                fontSize: 13, color: active === s.id ? LT.blu : LT.n600,
                background: active === s.id ? LT.bluSoft : 'transparent',
                fontWeight: active === s.id ? 600 : 500,
                textDecoration: 'none', marginBottom: 2,
                borderLeft: active === s.id ? `2px solid ${LT.blu}` : '2px solid transparent',
              }}
            >{s.label}</a>
          ))}
        </aside>

        <article style={{ flex: 1, minWidth: 0, maxWidth: 820 }}>
          {children}
        </article>
      </div>
    </div>
  );
}

// Selettore a due stati, come quello Classica / Settimanale della schermata
// Attività. Il segmento attivo si passa in `attivo`.
export const Segmenti = ({ voci = [], attivo }) => (
  <div style={{
    display: 'inline-flex', padding: 3, gap: 2,
    background: LT.n100, borderRadius: 999, border: `1px solid ${LT.n150}`,
  }}>
    {voci.map(v => (
      <span key={v} style={{
        padding: '4px 11px', borderRadius: 999, fontSize: 11.5, fontWeight: 700,
        background: v === attivo ? LT.n0 : 'transparent',
        color: v === attivo ? LT.blu : LT.n500,
        boxShadow: v === attivo ? LT.sh1 : 'none',
      }}>{v}</span>
    ))}
  </div>
);
